import type { BundleQuestionCategoryFragment } from "@stepapp/core";
import { CategoryType, V2CartQuestionsUpsert } from "@stepapp/core/apiTypes";
import { Block, Divider } from "@stepapp/ui";
import { useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";
import type { ReservationFormParams } from "../../store/draftReservation/types";
import ErrorMessage from "../ErrorMessage";
import CategoryQuestionAnswersSheet from "./CategoryQuestionAnswersSheet";
import CategoryQuestionItem from "./CategoryQuestionItem";
import { ServiceSceneDescription, ServiceSceneHeader } from "./ServiceSceneHeader";

type Props = {
  categoryQuestions: BundleQuestionCategoryFragment[];
  categoryType: CategoryType;
};

const CategoryQuestions = ({ categoryQuestions, categoryType }: Props) => {
  const [modalVisible, setModalVisible] = useState({
    isVisible: false,
    categoryId: "",
    questionId: "",
  });
  const {
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<ReservationFormParams>();

  const attributes = watch("attributes");

  const setNewAttribute = useCallback(
    (attributes: V2CartQuestionsUpsert[]) => {
      setValue("attributes", attributes, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    [setValue],
  );

  const handleCloseModal = useCallback(() => {
    setModalVisible({
      isVisible: false,
      categoryId: "",
      questionId: "",
    });
  }, []);

  const selectedQuestion =
    categoryQuestions
      ?.find((cat) => cat?.id === modalVisible?.categoryId)
      ?.questions?.find((question) => question?.id === modalVisible?.questionId) || undefined;

  return (
    <Block>
      {categoryQuestions?.map((category) => (
        <Block key={category?.id}>
          <ServiceSceneHeader name={category?.title} />
          <ServiceSceneDescription description={category?.description || ""} />

          <Block marginTop={3}>
            {category?.questions
              .concat()
              .sort((a, b) => a.position - b.position)
              .map(
                ({
                  id: questionId,
                  name,
                  description,
                  additionalDescription,
                  isFeatured,
                  isRequired,
                  answers,
                  type,
                  iconUrl,
                  numericType,
                }) => {
                  const errorMsg =
                    !!errors?.attributes?.message &&
                    isRequired &&
                    !attributes.some((a) => a?.attributeId === questionId)
                      ? errors?.attributes?.message
                      : "";

                  return (
                    <Block id={questionId} key={questionId} testID={`question-id-${questionId}`}>
                      <CategoryQuestionItem
                        description={description}
                        additionalDescription={additionalDescription}
                        questionId={questionId}
                        categoryId={category?.id}
                        name={name}
                        iconUrl={iconUrl}
                        isFeatured={isFeatured}
                        attributes={attributes}
                        answers={answers}
                        questionType={type}
                        numericType={numericType}
                        setModalVisible={setModalVisible}
                      />
                      <ErrorMessage message={errorMsg} />
                    </Block>
                  );
                },
              )}
          </Block>
          <Divider />
        </Block>
      ))}

      <CategoryQuestionAnswersSheet
        categoryType={categoryType}
        isVisible={modalVisible.isVisible}
        question={selectedQuestion}
        closeModal={handleCloseModal}
        attributes={attributes}
        setNewAttribute={setNewAttribute}
      />
    </Block>
  );
};

export default CategoryQuestions;
